import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Pack, PokemonCard, RarityStats, UserCard, ProbabilityFilterOptions } from '../types/pokemon';
import { useAuth } from './useAuth';
import { useTradeSettings } from './useTradeSettings';

// Nouvelles probabilités par position selon spec_stat.md (avec shiny)
const POSITION_PROBABILITIES_WITH_SHINY: Record<string, Record<string, number>> = {
  // Positions 1-3 (premières cartes)
  'first_three': {
    '♢': 1.0,    // 100%
    '♢♢': 0.0,   // 0%
    '♢♢♢': 0.0,  // 0%
    '♢♢♢♢': 0.0, // 0%
    '☆': 0.0,    // 0%
    '☆☆': 0.0,   // 0%
    '☆☆☆': 0.0,  // 0%
    '✧': 0.0,    // 0%
    '✧✧': 0.0,   // 0%
    '♛': 0.0,    // 0%
  },
  // Position 4
  'fourth': {
    '♢': 0.0,      // 0%
    '♢♢': 0.89,    // 89%
    '♢♢♢': 0.05,   // 5%
    '♢♢♢♢': 0.0167, // 1.67%
    '☆': 0.0257,   // 2.57%
    '☆☆': 0.005,   // 0.5%
    '☆☆☆': 0.0022, // 0.22%
    '✧': 0.00714,  // 0.714%
    '✧✧': 0.00333, // 0.333%
    '♛': 0.0004,   // 0.04%
  },
  // Position 5
  'fifth': {
    '♢': 0.0,      // 0%
    '♢♢': 0.56,    // 56%
    '♢♢♢': 0.19810,// 19.810%
    '♢♢♢♢': 0.0664,// 6.66%
    '☆': 0.1029,   // 10.29%
    '☆☆': 0.02,    // 2%
    '☆☆☆': 0.0089, // 0.89%
    '✧': 0.02857,  // 2.857%
    '✧✧': 0.01333, // 1.333%
    '♛': 0.0016,   // 0.16%
  }
};

// Anciennes probabilités pour les packs sans shiny
const POSITION_PROBABILITIES_NO_SHINY: Record<string, Record<string, number>> = {
  // Positions 1-3 (premières cartes)
  'first_three': {
    '♢': 1.0,    // 100%
    '♢♢': 0.0,   // 0%
    '♢♢♢': 0.0,  // 0%
    '♢♢♢♢': 0.0, // 0%
    '☆': 0.0,    // 0%
    '☆☆': 0.0,   // 0%
    '☆☆☆': 0.0,  // 0%
    '♛': 0.0,    // 0%
  },
  // Position 4
  'fourth': {
    '♢': 0.0,      // 0%
    '♢♢': 0.9,     // 90%
    '♢♢♢': 0.05,   // 5%
    '♢♢♢♢': 0.0167, // 1.67%
    '☆': 0.0257,   // 2.57%
    '☆☆': 0.005,   // 0.5%
    '☆☆☆': 0.0022, // 0.22%
    '♛': 0.0004,   // 0.04%
  },
  // Position 5
  'fifth': {
    '♢': 0.0,      // 0%
    '♢♢': 0.6,     // 60%
    '♢♢♢': 0.2,    // 20%
    '♢♢♢♢': 0.0666, // 6.66%
    '☆': 0.1029,   // 10.29%
    '☆☆': 0.02,    // 2%
    '☆☆☆': 0.0089, // 0.89%
    '♛': 0.0016,   // 0.16%
  }
};

// Fonction utilitaire pour normaliser les symboles d'étoiles
const normalizeRaritySymbol = (rarity: string): string => {
  // Convertir les symboles ★ en ☆ pour la correspondance avec POSITION_PROBABILITIES
  if (rarity === '★') return '☆';
  if (rarity === '★★') return '☆☆';
  if (rarity === '★★★') return '☆☆☆';
  
  return rarity;
};

// Remplacer la constante POSITION_PROBABILITIES par une fonction qui retourne les bonnes probabilités
const getPositionProbabilities = (cards: PokemonCard[]): Record<string, Record<string, number>> => {
  // Vérifier si le pack contient des cartes shiny
  const hasShinyCards = cards.some(card => card.rarity === '✧' || card.rarity === '✧✧');
  return hasShinyCards ? POSITION_PROBABILITIES_WITH_SHINY : POSITION_PROBABILITIES_NO_SHINY;
};

export function usePackProbabilities(setCode: string | undefined) {
  const [packs, setPacks] = useState<Pack[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { data: tradeSettings } = useTradeSettings();

  useEffect(() => {
    const fetchPacks = async () => {
      if (!setCode || !user) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        const { data: packData, error: packError } = await supabase
          .from('packs')
          .select('*')
          .eq('set', setCode);

        if (packError) throw packError;
        setPacks(packData || []);
      } catch (err) {
        console.error('Error fetching packs:', err);
        setError(err instanceof Error ? err.message : 'Une erreur est survenue');
        setPacks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPacks();
  }, [setCode, user?.id, user]);

  const calculatePackProbabilities = (
    cards: PokemonCard[], 
    collection: Record<number, UserCard>,
    filterOptions: ProbabilityFilterOptions = { 
      showMissing: true, 
      showLessThanTwo: false, 
      excludeTradable: false 
    }
  ) => {
    if (!cards || cards.length === 0 || !packs.length) return [];

    try {
      // Trouver les cartes "All"
      const allCards = cards.filter(card => 
        card.pack?.toLowerCase() === 'all'
      );

      // Vérifier si une carte est éligible à l'échange
      const isCardTradable = (card: PokemonCard) => {
        if (!tradeSettings) return false;
        
        return tradeSettings.some(rule => 
          rule.eligible_sets.includes(card.set || '') && 
          rule.eligible_rarities.includes(card.rarity)
        );
      };

      // Calculer les statistiques pour chaque pack du set
      const packProbabilities = packs.map(packInfo => {
        // Filtrer les cartes spécifiques à ce pack + les cartes "All"
        const packCards = [
          ...cards.filter(card => card.pack === packInfo.name),
          ...allCards
        ];
        
        // Si aucune carte n'est disponible pour ce pack, retourner des probabilités nulles
        if (packCards.length === 0) {
          return {
            pack: packInfo.name,
            packImage: packInfo.img,
            firstThreeCards: 0,
            fourthCard: 0,
            fifthCard: 0,
            global: 0,
            rarityStats: {}
          };
        }
        
        // Obtenir les probabilités appropriées pour ce pack
        const positionProbabilities = getPositionProbabilities(packCards);
        
        // Initialiser les statistiques par rareté
        const rarityStats: Record<string, RarityStats> = {};
        
        // Calculer les statistiques pour chaque rareté
        packCards.forEach(card => {
          // Normaliser la rareté de la carte
          const normalizedRarity = normalizeRaritySymbol(card.rarity);
          
          if (!rarityStats[normalizedRarity]) {
            rarityStats[normalizedRarity] = {
              total: 0,
              owned: 0,
              missing: 0,
              lessThanTwo: 0,
              tradable: 0
            };
          }
          
          rarityStats[normalizedRarity].total++;
          
          const userCard = collection[card.id];
          const cardTradable = isCardTradable(card);
          
          if (userCard && userCard.quantity > 0) {
            rarityStats[normalizedRarity].owned++;
            
            // Compter les cartes possédées en moins de 2 exemplaires
            if (userCard.quantity < 2) {
              rarityStats[normalizedRarity].lessThanTwo++;
            }
            
            // Compter les cartes disponibles à l'échange
            if (cardTradable) {
              rarityStats[normalizedRarity].tradable++;
            }
          } else {
            rarityStats[normalizedRarity].missing++;
          }
        });
        
        // Si la collection est vide, toutes les cartes sont manquantes
        const collectionIsEmpty = Object.keys(collection).length === 0;
        
        // Cas particulier : si toutes les cartes sont manquantes ou si la collection est vide
        const allCardsMissing = collectionIsEmpty || Object.values(rarityStats).every(stats => stats.owned === 0);
        
        if (allCardsMissing) {
          // Si la collection est vide, définir toutes les cartes comme manquantes
          if (collectionIsEmpty) {
            Object.keys(rarityStats).forEach(rarity => {
              rarityStats[rarity].missing = rarityStats[rarity].total;
              rarityStats[rarity].owned = 0;
            });
          }
          
          return {
            pack: packInfo.name,
            packImage: packInfo.img,
            firstThreeCards: 1,
            fourthCard: 1,
            fifthCard: 1,
            global: 1,
            rarityStats
          };
        }
        
        // Calculer les probabilités par position en fonction des options de filtrage
        let firstThreeCardsProbability = 0;
        let fourthCardProbability = 0;
        let fifthCardProbability = 0;
        
        // Calculer la probabilité pour les 3 premières cartes (toujours ♢)
        const firstThreeRarity = '♢';
        if (rarityStats[firstThreeRarity] && rarityStats[firstThreeRarity].total > 0) {
          // Calculer le nombre de cartes intéressantes selon les critères de filtrage
          let interestingCards = 0;
          
          if (filterOptions.showMissing) {
            interestingCards += rarityStats[firstThreeRarity].missing;
          }
          
          if (filterOptions.showLessThanTwo) {
            interestingCards += rarityStats[firstThreeRarity].lessThanTwo;
          }
          
          // Exclure les cartes tradables si demandé
          if (filterOptions.excludeTradable) {
            interestingCards -= rarityStats[firstThreeRarity].tradable;
          }
          
          // S'assurer que le nombre n'est pas négatif
          interestingCards = Math.max(0, interestingCards);
          
          const interestingRatio = interestingCards / rarityStats[firstThreeRarity].total;
          // Probabilité d'obtenir au moins une carte intéressante dans les 3 premières positions
          firstThreeCardsProbability = 1 - Math.pow(1 - interestingRatio, 3);
        }
        
                  // Calculer la probabilité pour la 4ème carte
          Object.entries(positionProbabilities.fourth).forEach(([rarity, probability]) => {
            // Normaliser la rareté pour la comparaison avec les cartes
            const normalizedRarity = normalizeRaritySymbol(rarity);
            
            // Vérifier si nous avons des cartes de cette rareté (normalisée ou non)
            const rarityToCheck = rarityStats[normalizedRarity] ? normalizedRarity : rarity;
            
            if (rarityStats[rarityToCheck] && rarityStats[rarityToCheck].total > 0 && probability > 0) {
              // Calculer le nombre de cartes intéressantes selon les critères de filtrage
              let interestingCards = 0;
              
              if (filterOptions.showMissing) {
                interestingCards += rarityStats[rarityToCheck].missing;
              }
              
              if (filterOptions.showLessThanTwo) {
                interestingCards += rarityStats[rarityToCheck].lessThanTwo;
              }
              
                            // Exclure les cartes tradables si demandé
              if (filterOptions.excludeTradable) {
                interestingCards -= rarityStats[rarityToCheck].tradable;
              }
              
              // S'assurer que le nombre n'est pas négatif
              interestingCards = Math.max(0, interestingCards);
              
              const interestingRatio = interestingCards / rarityStats[rarityToCheck].total;
              fourthCardProbability += probability * interestingRatio;
            }
          });
          
          // Calculer la probabilité pour la 5ème carte
          Object.entries(positionProbabilities.fifth).forEach(([rarity, probability]) => {
            // Normaliser la rareté pour la comparaison avec les cartes
            const normalizedRarity = normalizeRaritySymbol(rarity);
            
            // Vérifier si nous avons des cartes de cette rareté (normalisée ou non)
            const rarityToCheck = rarityStats[normalizedRarity] ? normalizedRarity : rarity;
            
            if (rarityStats[rarityToCheck] && rarityStats[rarityToCheck].total > 0 && probability > 0) {
              // Calculer le nombre de cartes intéressantes selon les critères de filtrage
              let interestingCards = 0;
              
              if (filterOptions.showMissing) {
                interestingCards += rarityStats[rarityToCheck].missing;
              }
              
              if (filterOptions.showLessThanTwo) {
                interestingCards += rarityStats[rarityToCheck].lessThanTwo;
              }
              
              // Exclure les cartes tradables si demandé
              if (filterOptions.excludeTradable) {
                interestingCards -= rarityStats[rarityToCheck].tradable;
              }
              
              // S'assurer que le nombre n'est pas négatif
              interestingCards = Math.max(0, interestingCards);
              
              const interestingRatio = interestingCards / rarityStats[rarityToCheck].total;
              fifthCardProbability += probability * interestingRatio;
            }
          });
        
        // Probabilité globale d'obtenir au moins une nouvelle carte dans un pack
        const globalProbability = 1 - (1 - firstThreeCardsProbability) * (1 - fourthCardProbability) * (1 - fifthCardProbability);
        
        return {
          pack: packInfo.name,
          packImage: packInfo.img,
          firstThreeCards: firstThreeCardsProbability,
          fourthCard: fourthCardProbability,
          fifthCard: fifthCardProbability,
          global: globalProbability,
          rarityStats
        };
      });
      
      // Trier par probabilité globale (décroissant)
      return packProbabilities.sort((a, b) => b.global - a.global);
    } catch (error) {
      console.error('Error calculating pack probabilities:', error);
      return [];
    }
  };

  return { packs, loading, error, calculatePackProbabilities };
} 